import { html, rune, View } from 'rune-ts';
import { LiveDataType } from '../../../../../features/Live/type';
import { makeEmptyScreenNavigate } from '../../../../../shared/app/navigate';
import { lazyLoad } from '../../../../../shared/client/global';
import { staticTypo, typoPcMo } from '../../../../../shared/typography/typo';
import { replaceNewlinesWithBr } from '../../../../../shared/util';
import { makeDefaultProfileImg } from '../../../../../shared/util/helper';
import { ButtonAction } from '../../../../../shared/components/atoms/ButtonAction/ButtonAction';
import { getResizedUrl } from '../../../../../shared/util/image';
import { MShopDefaultIcon } from '../../../../components/atoms/Icon';
import { ProductCardList } from '../../../../components/cells/ProductCardList/ProductCardList';
import klass from './CastOnAir.module.scss';
import { ProductCardSize } from '@services/MarppleShop/features/ProductList/type';

export type CastOnAirProps = { lives: LiveDataType[] };

const default_img = makeDefaultProfileImg(200);

class CastOnAirBodyView extends View<LiveDataType[]> {
  active_live: LiveDataType;
  active_index: number;

  constructor(data: LiveDataType[]) {
    super(data);

    this.active_index = 0;
    this.active_live = data[this.active_index]!;
  }

  override template() {
    const { platform, iframe_url, products, title, description, label_intl } = this.active_live;
    const queryString = iframe_url.includes('?') ? '&' : '?';
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    return html`<div class="${klass.content}">
      <div class="${klass.creator_list}">
        ${this.data.map((live, index) => {
          return html` <div
            class="creator ${klass.creator_button}"
            data-index="${index}"
            data-active="${index === this.active_index}"
          >
            <img
              src="${live.products?.[0]?.profile_image_url
                ? getResizedUrl({
                    url: live.products?.[0]?.profile_image_url,
                    width: 200,
                    quality: 90,
                    format: 'webp',
                  })
                : default_img}"
              alt="profile"
            />
          </div>`;
        })}
      </div>
      <div class="${klass.live_frame_container}">
        <div class="${klass.live_frame}">
          ${platform === 'youtube'
            ? html` <iframe
                data-src="${iframe_url}${queryString}mute=1&autoplay=1"
                class="don_lazy"
                title="YouTube video player"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen="false"
              ></iframe>`
            : platform === 'afreecatv'
              ? html` <iframe
                  data-src="${iframe_url}${queryString}autoplay=true"
                  class="don_lazy"
                  frameborder="0"
                  allowfullscreen="false"
                ></iframe>`
              : html` <iframe
                  data-src="${iframe_url}${queryString}muted=true&autoplay=true"
                  class="don_lazy"
                  frameborder="0"
                  allowfullscreen="false"
                  scrolling="no"
                ></iframe>`}
        </div>
      </div>
      <div class="${klass.live_content_container}">
        <div class="${klass.live_content}">
          <div class="${klass.body}">
            <div class="${staticTypo('bebas_32_bold')}">
              ${html.preventEscape(replaceNewlinesWithBr(title))}
            </div>
            <div class="${typoPcMo({ is_mobile, pc: '16_medium', mo: '14_medium' })}">
              ${html.preventEscape(replaceNewlinesWithBr(description))}
            </div>
          </div>
          ${this.active_live.shop_url && label_intl?.[ET.lang]
            ? new ButtonAction({
                href: this.active_live.shop_url,
                navigate: makeEmptyScreenNavigate(this.active_live.shop_url),
                size: is_mobile ? 'regular' : 'large',
                type: 'white',
                label: html`
                  <span class="${klass.button_label}">
                    ${html.preventEscape(label_intl?.[ET.lang])}
                    <span class="${klass.button_icon}">${MShopDefaultIcon()}</span>
                  </span>
                `,
                fit: true,
              })
            : ''}
        </div>
        ${new ProductCardList(
          { products },
          {
            resize_width: 600,
            is_horizontal: true,
            size: ProductCardSize.medium,
            klass: klass.product,
            container_klass: klass.product_container,
            hide_badges: is_mobile,
            hide_tag: is_mobile,
          },
        )}
      </div>
    </div> `;
  }

  protected override onRender() {
    this.delegate('click', '.creator', (e) => {
      const el = e.currentTarget as HTMLElement;
      const index = parseInt(el.dataset.index ?? '');

      if (isNaN(index) || index === this.active_index) return;

      this.active_live = this.data[index]!;
      this.active_index = index;
      this.redraw();
      lazyLoad();
    });
  }
}

export class CastOnAir extends View<CastOnAirProps> {
  override template() {
    const { lives } = this.data;

    return html`<div class="${klass.container}">${new CastOnAirBodyView(lives)}</div> `;
  }
}
